import { tutorialTaskDone } from '../../tutorial/events'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import { Screen } from '../components/Screen'
import { Field, useLeaveGuard } from '../components/Form'
import { LimitNote, limitReason, type LimitReason } from '../components/LimitNote'
import { SportFieldsPreview, SportSelect, useSportTypes } from '../components/SportFields'
import { EmptyState, ErrorState, SaveFailedAlert, Sheet, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { useClubOverview, useDirectionContents, useSingleFlight, useSportFields } from '../data/hooks'
import { SliceApiError, type ClubOverview, type DirectionContents, type SportOption } from '../data/types'
import { useBackTo } from './AthleteScreen'

type Direction = ClubOverview['directions'][number]

const NAME_TAKEN = 'Такое название уже занято: выберите другое'

/** «3 спортсмена», «1 группа»: число и слово. */
export function countOf(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10
  const m100 = n % 100
  if (m10 === 1 && m100 !== 11) return `${n} ${one}`
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return `${n} ${few}`
  return `${n} ${many}`
}

/** После смены, переименования или удаления направления: всё перечитать. */
function useRefreshAll() {
  const client = useQueryClient()
  return () => client.invalidateQueries({ queryKey: ['redesign'] })
}

// ---------------------------------------------------------------------------
// Новое направление вне мастера (/club/directions/new)
// ---------------------------------------------------------------------------

/**
 * S11: новое направление - вид спорта с предпросмотром полей, как в мастере.
 * Созданное становится выбранным: расписание, группы и спортсмены дальше
 * показываются для него. Лимит тарифа - правило с путём в подписку.
 */
export function DirectionNewScreen() {
  const { api, toast } = useEnv()
  const navigate = useNavigate()
  const client = useQueryClient()
  const sports = useSportTypes()
  const flight = useSingleFlight()
  const [sportName, setSportName] = useState('')
  const [name, setName] = useState('')
  const [failure, setFailure] = useState<LimitReason | 'failed' | null>(null)
  const [nameError, setNameError] = useState<string>()
  const sport: SportOption | undefined = sports.data?.find((s) => s.name === sportName)
  const dirty = !!(sportName || name.trim())
  const leave = useLeaveGuard(dirty)
  const toList = () => navigate('/club/directions', { replace: true })
  const back = () => leave.guard(toList)

  const create = async () => {
    if (!sport || !name.trim()) return
    setFailure(null)
    setNameError(undefined)
    try {
      // Всё до ухода с экрана - в одном полёте: второе нажатие не создаст второе
      await flight.run(async () => {
        await api.createDirection(name.trim(), sport)
        tutorialTaskDone('direction')
        // Выбрано новое направление: всё, что показано, - уже про него
        await client.resetQueries({ queryKey: ['redesign'] })
        toast(`Направление создано и выбрано: ${name.trim()}`)
        toList()
      })
    } catch (e) {
      if (e instanceof SliceApiError && e.message === 'name_taken') setNameError(NAME_TAKEN)
      else setFailure(limitReason(e))
    }
  }

  return (
    <Screen
      title="Новое направление"
      back={back}
      hideHub
      hideNav
      actionBar={
        <>
          <button type="button" className="pc-btn" style={{ flex: 1 }} onClick={back}>
            Отмена
          </button>
          <button
            type="button"
            className="pc-btn pc-btn--primary"
            style={{ flex: 2 }}
            disabled={flight.busy || !sport || !name.trim()}
            onClick={() => void create()}
          >
            {flight.busy ? 'Создаю…' : 'Создать'}
          </button>
        </>
      }
    >
      <p className="pc-muted" style={{ margin: '12px 0 0', fontSize: 14 }}>
        Направление - вид спорта внутри клуба со своими группами, спортсменами и тренерами. Новое станет выбранным.
      </p>
      {sports.isPending ? (
        <Skeleton rows={2} label="Загружаю виды спорта" />
      ) : sports.isError ? (
        <ErrorState title="Не удалось загрузить виды спорта" onRetry={() => void sports.refetch()} />
      ) : (
        <form
          className="pc-form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            void create()
          }}
        >
          <SportSelect
            sports={sports.data}
            value={sportName}
            onChange={(next) => {
              setSportName(next)
              if (!name.trim() || name === sportName) setName(next)
            }}
          />
          <Field label="Название направления" hint="Например: «Каратэ Киокушинкай» или «ОФП для малышей»" error={nameError}>
            {(p) => <input {...p} className="pc-input" value={name} maxLength={200} autoComplete="off" onChange={(e) => setName(e.target.value)} />}
          </Field>
          {sportName && <SportFieldsPreview sportName={sportName} />}
          {failure === 'failed' && <SaveFailedAlert>Направление не создано, попробуйте ещё раз.</SaveFailedAlert>}
          {(failure === 'limit' || failure === 'locked') && <LimitNote what="направлений" reason={failure} />}
        </form>
      )}
      {leave.dialog}
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// Направление (/club/directions/:id): вид спорта, поля, правка, удаление
// ---------------------------------------------------------------------------

function useDirection(id: string) {
  const overview = useClubOverview()
  const direction = overview.data?.directions.find((d) => d.id === id)
  return { overview, direction }
}

export function DirectionScreen() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { api, toast } = useEnv()
  const client = useQueryClient()
  const back = useBackTo('/club/directions')
  const { overview, direction } = useDirection(id)
  const [deleting, setDeleting] = useState(false)
  const flight = useSingleFlight()

  if (overview.isPending) {
    return (
      <Screen title="Направление" back={back}>
        <Skeleton rows={3} />
      </Screen>
    )
  }
  if (overview.isError) {
    return (
      <Screen title="Направление" back={back}>
        <ErrorState title="Не удалось загрузить направление" onRetry={() => void overview.refetch()} />
      </Screen>
    )
  }
  if (!direction) {
    return (
      <Screen title="Направление" back={back}>
        <EmptyState
          title="Направления нет"
          text="Его удалили или оно в другом клубе."
          action={
            <button type="button" className="pc-btn" onClick={() => navigate('/club/directions', { replace: true })}>
              Все направления
            </button>
          }
        />
      </Screen>
    )
  }

  const choose = async () => {
    try {
      await flight.run(async () => {
        await api.switchDirection(direction.id)
        await client.resetQueries({ queryKey: ['redesign'] })
        toast(`Переключено: ${direction.name}`)
        navigate('/today', { replace: true })
      })
    } catch {
      toast('Не удалось переключить. Попробуйте ещё раз.')
    }
  }

  return (
    <Screen title={direction.name} subtitle="Направление" back={back}>
      <div className="pc-card" style={{ marginTop: 12 }}>
        <div className="pc-kv__label">Вид спорта</div>
        <div className="pc-kv__value" style={{ fontSize: 18 }}>
          {direction.sportName || 'Не указан'}
        </div>
        {direction.current ? (
          <span className="pc-badge pc-badge--success" style={{ marginTop: 8 }}>
            <Check size={14} aria-hidden="true" /> Выбрано сейчас
          </span>
        ) : (
          <button type="button" className="pc-btn pc-btn--block" style={{ marginTop: 8 }} disabled={flight.busy} onClick={() => void choose()}>
            {flight.busy ? 'Переключаю…' : 'Сделать выбранным'}
          </button>
        )}
      </div>

      {direction.sportName && <SportFieldsPreview sportName={direction.sportName} />}

      <button
        type="button"
        className="pc-btn pc-btn--block"
        style={{ marginTop: 16 }}
        onClick={() => navigate(`/club/directions/${direction.id}/edit`)}
      >
        Изменить название или вид спорта
      </button>
      <button type="button" className="pc-btn pc-btn--block pc-btn--danger" onClick={() => setDeleting(true)}>
        Удалить направление
      </button>

      {deleting && <DeleteDirectionSheet direction={direction} all={overview.data.directions} onClose={() => setDeleting(false)} />}
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// Правка: название и вид спорта (/club/directions/:id/edit)
// ---------------------------------------------------------------------------

export function DirectionEditScreen() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { overview, direction } = useDirection(id)
  const sports = useSportTypes()
  const back = () => navigate(`/club/directions/${id}`, { replace: true })

  let body
  if (overview.isPending || sports.isPending) body = <Skeleton rows={3} />
  else if (overview.isError) body = <ErrorState title="Не удалось загрузить направление" onRetry={() => void overview.refetch()} />
  else if (sports.isError) body = <ErrorState title="Не удалось загрузить виды спорта" onRetry={() => void sports.refetch()} />
  else if (!direction) body = <EmptyState title="Направления нет" text="Его удалили или оно в другом клубе." />
  else return <DirectionEditForm direction={direction} sports={sports.data} />

  return (
    <Screen title="Правка направления" back={back} hideHub hideNav>
      {body}
    </Screen>
  )
}

function DirectionEditForm({ direction: d, sports }: { direction: Direction; sports: SportOption[] }) {
  const navigate = useNavigate()
  const { api, toast } = useEnv()
  const refresh = useRefreshAll()
  const flight = useSingleFlight()
  // Исходное - снимок на открытие: перечитанное фоном не сбрасывает правку.
  const [initial] = useState(() => ({ name: d.name, sportName: d.sportName }))
  const [name, setName] = useState(initial.name)
  const [sportName, setSportName] = useState(initial.sportName)
  const [nameError, setNameError] = useState<string>()
  const [failure, setFailure] = useState<'failed' | 'gone' | 'locked' | null>(null)
  const sportChanged = sportName !== initial.sportName
  const dirty = name.trim() !== initial.name || sportChanged
  const leave = useLeaveGuard(dirty)
  const toDirection = () => navigate(`/club/directions/${d.id}`, { replace: true })
  const back = () => leave.guard(toDirection)

  const save = async () => {
    if (!name.trim()) {
      setNameError('Название не может быть пустым')
      return
    }
    setNameError(undefined)
    setFailure(null)
    const sport = sportChanged ? sports.find((s) => s.name === sportName) : undefined
    try {
      await flight.run(async () => {
        await api.updateDirection(d.id, {
          ...(name.trim() !== initial.name ? { name: name.trim() } : {}),
          ...(sport ? { sport } : {}),
        })
        await refresh()
        toast('Изменения сохранены')
        toDirection()
      })
    } catch (e) {
      if (e instanceof SliceApiError && e.message === 'name_taken') setNameError(NAME_TAKEN)
      else if (e instanceof SliceApiError && e.kind === 'not_found') setFailure('gone')
      else if (limitReason(e) === 'locked') setFailure('locked')
      else setFailure('failed')
    }
  }

  return (
    <Screen
      title="Правка направления"
      subtitle={d.name}
      back={back}
      hideHub
      hideNav
      actionBar={
        <>
          <button type="button" className="pc-btn" style={{ flex: 1 }} onClick={back}>
            Отмена
          </button>
          <button type="button" className="pc-btn pc-btn--primary" style={{ flex: 2 }} disabled={flight.busy || !dirty} onClick={() => void save()}>
            {flight.busy ? 'Сохраняю…' : 'Сохранить'}
          </button>
        </>
      }
    >
      <form
        className="pc-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        {failure === 'failed' && <SaveFailedAlert>Изменения остались на экране, попробуйте ещё раз.</SaveFailedAlert>}
        {failure === 'gone' && <SaveFailedAlert>Направление уже удалено: сохранить некуда.</SaveFailedAlert>}
        {failure === 'locked' && (
          <div className="pc-note" role="alert">
            Подписка клуба закончилась: клуб сейчас только для чтения. Ничего не изменено.
          </div>
        )}
        <Field label="Название направления" error={nameError}>
          {(p) => <input {...p} className="pc-input" value={name} maxLength={200} autoComplete="off" onChange={(e) => setName(e.target.value)} />}
        </Field>
        <SportSelect sports={sports} value={sportName} onChange={setSportName} />
      </form>
      {sportChanged && <SportChangeNote directionId={d.id} from={initial.sportName} to={sportName} />}
      {leave.dialog}
    </Screen>
  )
}

/**
 * Что станет со спортивными данными при смене вида спорта - по тому, как
 * устроено: сервер sport_data не трогает, подписи и поля карточки берутся из
 * шаблона нового вида спорта, ключи вне шаблона показываются в спортивных
 * данных отдельным разделом под служебными названиями.
 */
function SportChangeNote({ directionId, from, to }: { directionId: string; from: string; to: string }) {
  const oldFields = useSportFields(from || undefined)
  const newFields = useSportFields(to || undefined)
  const contents = useDirectionContents(directionId)
  const oldKeys = new Set((oldFields.data ?? []).map((f) => f.field_key))
  const shared = (newFields.data ?? []).filter((f) => oldKeys.has(f.field_key))
  const sharedKeys = new Set(shared.map((f) => f.field_key))
  const lost = (oldFields.data ?? []).filter((f) => !sharedKeys.has(f.field_key))
  const fresh = (newFields.data ?? []).filter((f) => !oldKeys.has(f.field_key))
  const athletes = contents.data?.athletes.length

  return (
    <>
      <SportFieldsPreview sportName={to} title={`Поля «${to}»`} />
      <div className="pc-aa-note" aria-label="Что станет со спортивными данными">
        <b>
          Что станет со спортивными данными
          {athletes !== undefined ? ` (${countOf(athletes, 'спортсмен', 'спортсмена', 'спортсменов')})` : ''}
        </b>
        <span>- уже записанные значения не стираются;</span>
        {shared.length > 0 && (
          <span>- общие поля сохранят значения с подписями «{to}»: {shared.map((f) => f.field_label).join(', ')};</span>
        )}
        {lost.length > 0 && (
          <span>
            - поля «{from}» ({lost.map((f) => f.field_label).join(', ')}) уйдут из карточки, значения останутся в спортивных
            данных спортсмена в разделе «Поля из прежней версии» - под служебными названиями (
            {lost.map((f) => f.field_key).join(', ')});
          </span>
        )}
        {fresh.length > 0 && <span>- поля «{to}» будут пустыми, заполнить - в спортивных данных спортсмена.</span>}
        <span className="pc-muted">Вернуть прежний вид спорта можно так же: подписи вернутся.</span>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Удаление: точный предпросмотр и серьёзное подтверждение
// ---------------------------------------------------------------------------

/**
 * Удаление направления - по тому, как устроен сервер: одним запросом мягко
 * удаляются направление, его группы, спортсмены и справочник тренеров (место
 * в лимитах тарифа освобождается); посещения, отметки оплат, события,
 * материалы и записи остаются в базе без экрана. Без загруженного содержимого
 * предпросмотр нечестный - удалить нельзя. Подтверждение - названием.
 */
export function DeleteDirectionSheet({
  direction: d,
  all,
  onClose,
}: {
  direction: Direction
  all: Direction[]
  onClose: () => void
}) {
  const navigate = useNavigate()
  const { api, toast } = useEnv()
  const client = useQueryClient()
  const contents = useDirectionContents(d.id)
  const flight = useSingleFlight()
  const [typed, setTyped] = useState('')
  const [failed, setFailed] = useState(false)
  const next = all.find((x) => x.id !== d.id)
  const matches = typed.trim().toLocaleLowerCase('ru') === d.name.trim().toLocaleLowerCase('ru')

  const finish = async (text: string) => {
    toast(text)
    // Сначала уходим с экрана: иначе перечитывание спросило бы содержимое
    // удалённого направления
    navigate(d.current ? '/today' : '/club/directions', { replace: true })
    client.removeQueries({ queryKey: ['redesign', 'direction-contents', d.id] })
    // Удалено выбранное - выбрано другое (или никакое - тогда откроется мастер)
    if (d.current) await client.resetQueries({ queryKey: ['redesign'] })
    else await client.invalidateQueries({ queryKey: ['redesign'] })
  }

  const remove = async () => {
    if (!matches) return
    setFailed(false)
    try {
      await flight.run(async () => {
        await api.deleteDirection(d.id)
        await finish(`Направление удалено: ${d.name}`)
      })
    } catch (e) {
      // Ответ на первое удаление потерялся, а направление уже удалено - это успех.
      if (e instanceof SliceApiError && e.kind === 'not_found') await finish(`Направление уже удалено: ${d.name}`)
      else setFailed(true)
    }
  }

  return (
    <Sheet title={`Удалить направление «${d.name}»?`} onClose={onClose}>
      {contents.isPending ? (
        <Skeleton rows={2} label="Проверяю, что в направлении" />
      ) : contents.isError ? (
        <ErrorState
          title="Не удалось проверить, что в направлении"
          text="Без этого не показать, что пропадёт. Удаление недоступно, пока не загрузится."
          onRetry={() => void contents.refetch()}
        />
      ) : (
        <DeletePreview contents={contents.data} direction={d} next={next} />
      )}
      {contents.isSuccess && (
        <>
          <Field label={`Чтобы подтвердить, введите название: ${d.name}`}>
            {(p) => <input {...p} className="pc-input" value={typed} autoComplete="off" onChange={(e) => setTyped(e.target.value)} />}
          </Field>
          {failed && <SaveFailedAlert>Направление не удалено, попробуйте ещё раз.</SaveFailedAlert>}
          <button
            type="button"
            className="pc-btn pc-btn--block pc-btn--danger"
            style={{ marginTop: 12 }}
            disabled={flight.busy || !matches}
            onClick={() => void remove()}
          >
            {flight.busy ? 'Удаляю…' : 'Удалить направление'}
          </button>
        </>
      )}
      <button type="button" className="pc-btn pc-btn--block" onClick={onClose}>
        Отмена
      </button>
    </Sheet>
  )
}

function DeletePreview({ contents: c, direction: d, next }: { contents: DirectionContents; direction: Direction; next?: Direction }) {
  const names = useMemo(() => c.athletes.map((a) => a.name).join(', '), [c.athletes])
  const empty = c.groups.length === 0 && c.athletes.length === 0 && c.coaches.length === 0
  return (
    <>
      <div className="pc-aa-note pc-aa-note--danger">
        <b>Пропадёт вместе с направлением</b>
        {empty && <span>В направлении нет групп, спортсменов и тренеров.</span>}
        {c.groups.length > 0 && (
          <span>
            - {countOf(c.groups.length, 'группа', 'группы', 'групп')}:{' '}
            {c.groups.map((g) => `${g.name} (${g.athletes})`).join(', ')} - с расписанием, журналом и оплатами;
          </span>
        )}
        {c.athletes.length > 0 && (
          <span>
            - спортсмены направления, и на паузе тоже ({c.athletes.length}): {names};
          </span>
        )}
        {c.coaches.length > 0 && (
          <span>
            - тренеры справочника: {c.coaches.map((x) => x.name).join(', ')};
          </span>
        )}
        <span>- занятия направления из «Сегодня» и расписания, разговор Штаба в направлении.</span>
      </div>
      <div className="pc-aa-note">
        <b>Остаётся в базе, но открыть будет негде</b>
        <span>- отметки посещений и оплат, записи о спортсменах и группах;</span>
        <span>
          - {c.events > 0 ? `соревнования и сборы (${c.events})` : 'соревнования и сборы'} и материалы направления.
        </span>
        <span>Место в лимитах тарифа освободится. Участники клуба остаются в клубе.</span>
      </div>
      <p className="pc-muted" style={{ margin: '12px 0 0', fontSize: 14 }}>
        {d.current
          ? next
            ? `Это выбранное направление: после удаления откроется «${next.name}». `
            : 'Это последнее направление клуба: после удаления откроется настройка нового. '
          : ''}
        Вернуть направление из приложения нельзя.
      </p>
    </>
  )
}
